import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut } from "aws-amplify/auth";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import awsExports from './aws-exports';

import Login, { REMEMBER_DEVICE_STORAGE_KEY } from "./pages/Login";
import CampaignDashboard from "./pages/CampaignDashboard";
import EspDashboard from "./pages/EspDashboard";
import MainDashboard from "./pages/MainDashboard";
import DailyOverviewPage from "./pages/DailyOverviewPage";
import ApiDashboard from "./pages/ApiDashboard";
import ImportsPage from "./pages/ImportsPage";
import ActiveEspsPage from "./pages/ActiveEspsPage";
import IncomingEmails from "./pages/IncomingEmails";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import WebhookCampaignStatsPage from "./pages/WebhookCampaignStatsPage";
import SimplyloanPage from "./pages/SimplyloanPage";
import SimplyloanEmailPage from "./pages/SimplyloanEmailPage";
import SimplyloanSmsPage from "./pages/SimplyloanSmsPage";
import SimplyloanRedirectAdminPage from "./pages/SimplyloanRedirectAdminPage";
import SimplyloanRedirectTrackingPage from "./pages/SimplyloanRedirectTrackingPage";
import SimplyloanSeoPage from "./pages/SimplyloanSeoPage";
import SimplyloanMetaDashboard from "./pages/SimplyloanMetaDashboard";
import SimplyloanMetaCampaigns from "./pages/SimplyloanMetaCampaigns";
import SimplyloanGoogleDashboard from "./pages/SimplyloanGoogleDashboard";
import SimplyloanGoogleCampaigns from "./pages/SimplyloanGoogleCampaigns";
import SimplyloanMicrosoftDashboard from "./pages/SimplyloanMicrosoftDashboard";
import SimplyloanMicrosoftCampaigns from "./pages/SimplyloanMicrosoftCampaigns";
import SimplyloanGeoOptimizer from "./pages/SimplyloanGeoOptimizer";
import SimplyloanLtvDashboard from "./pages/SimplyloanLtvDashboard";
import AffiliateLinks from "./pages/AffiliateLinks";
import AutoScheduleDashboard from "./pages/AutoScheduleDashboard";
import CampaignHub from "./pages/CampaignHub";
import TextLibrary from "./pages/TextLibrary";
import DailyReports from "./pages/DailyReports";
import AccountingPage from "./pages/AccountingPage";
import AccountingInvoicesPage from "./pages/AccountingInvoicesPage";
import AccountingEspPage from "./pages/AccountingEspPage";
import AccountingRenewalsPage from "./pages/AccountingRenewalsPage";
import LeadIntakeDashboard from "./pages/LeadIntakeDashboard";
import ConverterIntelligence from "./pages/ConverterIntelligence";
import SvenskalånSeoPage from "./pages/SvenskalånSeoPage";
import DispatchMatrix from "./pages/DispatchMatrix";
import FeedblitzDomainRotationPage from "./pages/FeedblitzDomainRotationPage";
import TasksPage from "./pages/TasksPage";
import SmartResponseMetaDashboard from "./pages/smartresponse/SmartResponseMetaDashboard";
import SmartResponseCampaignBuilder from "./pages/smartresponse/SmartResponseCampaignBuilder";
import SmartResponseExclusionHub from "./pages/smartresponse/SmartResponseExclusionHub";
import SmartResponsePortalAdmin from "./pages/smartresponse/SmartResponsePortalAdmin";
import MailCleanerPage from "./pages/MailCleanerPage";


Amplify.configure(awsExports);

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// --- Auto Logout Hook ---
function useAutoLogout(timeoutMs: number = 15 * 60 * 1000) {
  useEffect(() => {
    const triggerIdleLogout = () => {
      try {
        if (window.location.pathname === "/login") return;
        // Remembered devices stay signed in for the full 30-day Cognito RefreshToken lifetime
        if (localStorage.getItem(REMEMBER_DEVICE_STORAGE_KEY) !== "false") return;
      } catch {
        return;
      }
      alert("Logging out due to inactivity.");
      signOut().then(() => {
        window.location.href = "/login";
      });
    };

    let timer = setTimeout(triggerIdleLogout, timeoutMs);

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(triggerIdleLogout, timeoutMs);
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
    };
  }, [timeoutMs]);
}

// --- Protected Route Wrapper ---
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const isLocalhost =
    process.env.NODE_ENV === 'development' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  useEffect(() => {
    getCurrentUser()
      .then(() => setAuthorized(true))
      .catch(() => setAuthorized(false));
  }, []);

  if (authorized === null && !isLocalhost) return null;
  return (authorized || isLocalhost) ? <>{children}</> : <Navigate to="/login" />;
}

// --- Role Protected Route Wrapper ---
function RoleProtectedRoute({ children, allowedEmails }: { children: React.ReactNode, allowedEmails: string[] }) {
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const isLocalhost =
    process.env.NODE_ENV === 'development' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  useEffect(() => {
    if (isLocalhost) {
      setAuthorized(true);
      return;
    }

    getCurrentUser()
      .then((user) => {
        const email = (user.signInDetails?.loginId || user.username || "").trim().toLowerCase();
        const normalizedAllowed = allowedEmails.map((e) => e.trim().toLowerCase());
        if (normalizedAllowed.includes(email)) {
          setAuthorized(true);
        } else {
          setAuthorized(false);
        }
      })
      .catch(() => {
        setAuthorized(isLocalhost);
      });
  }, [allowedEmails, isLocalhost]);

  if (authorized === null && !isLocalhost) return null;
  return (authorized || isLocalhost) ? <>{children}</> : <Navigate to="/" />; // Redirect to dashboard if not allowed
}

// --- Main App ---
export default function App() {
  useAutoLogout(); // 15-minute idle timeout

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Dashboard Route (Root -> Stats / Revenue Dashboard) */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/overview"
            element={
              <ProtectedRoute>
                <DailyOverviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-overview"
            element={
              <ProtectedRoute>
                <DailyOverviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <MainDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/revenue"
            element={
              <ProtectedRoute>
                <MainDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wct-dashboard"
            element={
              <ProtectedRoute>
                <MainDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/api-dashboard"
            element={
              <ProtectedRoute>
                <ApiDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <TasksPage />
              </ProtectedRoute>
            }
          />

          {/* Email Pages (Protected) */}
          <Route
            path="/email/domain-rotation"
            element={
              <ProtectedRoute>
                <FeedblitzDomainRotationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email/feedblitz-domains"
            element={
              <ProtectedRoute>
                <FeedblitzDomainRotationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email/campaigns/auto-scheduler"
            element={
              <Navigate to="/email/auto-schedule" replace />
            }
          />
          <Route
            path="/email/auto-schedule"
            element={
              <ProtectedRoute>
                <AutoScheduleDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email/scheduler-intelligence"
            element={
              <ProtectedRoute>
                <AutoScheduleDashboard initialTab="intelligence" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email/dispatch-matrix"
            element={
              <ProtectedRoute>
                <DispatchMatrix />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dispatch-matrix"
            element={
              <ProtectedRoute>
                <DispatchMatrix />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sendout-plan"
            element={
              <ProtectedRoute>
                <DispatchMatrix />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email/campaigns"
            element={
              <ProtectedRoute>
                <CampaignDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email/esp-dashboard"
            element={
              <ProtectedRoute>
                <EspDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/email/imports"
            element={
              <ProtectedRoute>
                <ImportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email/active-esps"
            element={
              <ProtectedRoute>
                <ActiveEspsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email/incoming"
            element={
              <ProtectedRoute>
                <IncomingEmails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/converters"
            element={
              <ProtectedRoute>
                <ConverterIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email/campaign-stats"
            element={
              <ProtectedRoute>
                <WebhookCampaignStatsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan"
            element={
              <ProtectedRoute>
                <SimplyloanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/sms"
            element={
              <ProtectedRoute>
                <SimplyloanSmsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/email"
            element={
              <ProtectedRoute>
                <SimplyloanEmailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/redirect-admin"
            element={
              <ProtectedRoute>
                <SimplyloanRedirectAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/redirect-tracking"
            element={
              <ProtectedRoute>
                <SimplyloanRedirectTrackingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/meta"
            element={
              <ProtectedRoute>
                <SimplyloanMetaDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/meta/campaigns"
            element={
              <ProtectedRoute>
                <SimplyloanMetaCampaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/google"
            element={
              <ProtectedRoute>
                <SimplyloanGoogleDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/google/campaigns"
            element={
              <ProtectedRoute>
                <SimplyloanGoogleCampaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/microsoft"
            element={
              <ProtectedRoute>
                <SimplyloanMicrosoftDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/microsoft/campaigns"
            element={
              <ProtectedRoute>
                <SimplyloanMicrosoftCampaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/optimizer"
            element={
              <ProtectedRoute>
                <SimplyloanGeoOptimizer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/ltv"
            element={
              <ProtectedRoute>
                <SimplyloanLtvDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/ltv-cohorts"
            element={
              <ProtectedRoute>
                <SimplyloanLtvDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simplyloan/seo"
            element={
              <RoleProtectedRoute allowedEmails={["kristoffer@afima.dk"]}>
                <SimplyloanSeoPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/seo/simplyloan"
            element={
              <Navigate to="/simplyloan/seo" replace />
            }
          />

          <Route
            path="/campaigns/affiliate-links"
            element={
              <ProtectedRoute>
                <AffiliateLinks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns"
            element={
              <ProtectedRoute>
                <CampaignHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/text-library"
            element={
              <ProtectedRoute>
                <TextLibrary />
              </ProtectedRoute>
            }
          />

          <Route
            path="/email/daily-reports"
            element={
              <ProtectedRoute>
                <DailyReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email/cleaner"
            element={
              <RoleProtectedRoute allowedEmails={["kristoffer@afima.dk"]}>
                <MailCleanerPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/mail-cleaner"
            element={
              <RoleProtectedRoute allowedEmails={["kristoffer@afima.dk"]}>
                <MailCleanerPage />
              </RoleProtectedRoute>
            }
          />

          <Route
            path="/accounting"
            element={
              <RoleProtectedRoute allowedEmails={["kristoffer@afima.dk"]}>
                <AccountingPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/accounting/renewals"
            element={
              <RoleProtectedRoute allowedEmails={["kristoffer@afima.dk"]}>
                <AccountingRenewalsPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/accounting/invoices"
            element={
              <RoleProtectedRoute allowedEmails={["kristoffer@afima.dk"]}>
                <AccountingInvoicesPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/accounting/esp"
            element={
              <RoleProtectedRoute allowedEmails={["kristoffer@afima.dk"]}>
                <AccountingEspPage />
              </RoleProtectedRoute>
            }
          />

          <Route
            path="/leads"
            element={
              <RoleProtectedRoute allowedEmails={["kristoffer@afima.dk"]}>
                <LeadIntakeDashboard />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/leads/hygiene"
            element={
              <RoleProtectedRoute allowedEmails={["kristoffer@afima.dk"]}>
                <LeadIntakeDashboard />
              </RoleProtectedRoute>
            }
          />

          {/* Svenskalaan SEO */}
          <Route
            path="/svenskalaan/seo"
            element={
              <RoleProtectedRoute allowedEmails={["kristoffer@afima.dk"]}>
                <SvenskalånSeoPage />
              </RoleProtectedRoute>
            }
          />

          {/* PremiePortalen (Multi-Provider Konkurrenceportal & Meta Lead Gen) */}
          <Route
            path="/premieportalen"
            element={
              <ProtectedRoute>
                <SmartResponsePortalAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/premieportalen/portal"
            element={
              <ProtectedRoute>
                <SmartResponsePortalAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/premieportalen/leads"
            element={
              <ProtectedRoute>
                <SmartResponsePortalAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/premieportalen/meta"
            element={
              <ProtectedRoute>
                <SmartResponseMetaDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/premieportalen/campaigns"
            element={
              <ProtectedRoute>
                <SmartResponseCampaignBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/premieportalen/exclusions"
            element={
              <ProtectedRoute>
                <SmartResponseExclusionHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smartresponse"
            element={
              <ProtectedRoute>
                <SmartResponsePortalAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smartresponse/meta"
            element={
              <ProtectedRoute>
                <SmartResponseMetaDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smartresponse/campaigns"
            element={
              <ProtectedRoute>
                <SmartResponseCampaignBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smartresponse/exclusions"
            element={
              <ProtectedRoute>
                <SmartResponseExclusionHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/smartresponse/portal"
            element={
              <ProtectedRoute>
                <SmartResponsePortalAdmin />
              </ProtectedRoute>
            }
          />

          {/* Fallback Route */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}
